/** leads/LeadCandidatePage.tsx — 벤처 후보군(공공데이터 명단) 관리: 업로드→필터→리드 승격
 *  2026-09-10 올댓벤처스 「벤처인증 기업」 표와 같은 열·검색·열 폭 규칙 (No · 회사명 · 대표자 · 사업자번호 · 업종 · 지역 · 설립 · 연매출 · 영업이익 · 유효기간(전체 이력) · 임박도)
 *  + 영업 DB 고유 기능 유지: 유형 칩 · 체크 선택(삭제·스캔) · 리드 승격 · 법인번호 필터 · CSV 업로드 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './leads.css';
import { listCandidates, candidateMeta, uploadCandidateCsv, deleteCandidates } from './api';
import type { Candidate, CandidateFilters, CandidateMeta } from './types';
import CandidatePromoteModal from './CandidatePromoteModal';
import ScanControl from './ScanControl';
import { setCandidateFilters } from './filterBus';
import UploadAnomalies from './UploadAnomalies';
import type { UploadAnomaly } from './types';
import { fmtBizNo } from './calc';
import AtvTable, { AtvPager, FinCell } from './AtvTable';
import type { AtvCol } from './AtvTable';
import AtvFilterCard, { rangeLabel } from './AtvFilterCard';
import type { Chip, RangeValues } from './AtvFilterCard';

interface Props { onToast: (m: string, t?: string) => void }

const TYPE_ORDER = ['벤처투자유형', '혁신성장유형', '연구개발유형', '예비벤처유형'];
const FIN_RANGES = [
  { key: 'sale', label: '연매출', presets: [{ label: '10억 이상', min: '100000' }, { label: '50억 이상', min: '500000' }, { label: '100억 이상', min: '1000000' }, { label: '10억 미만', max: '100000' }] },
  { key: 'op', label: '영업이익', unit: '만원 · 적자는 -', signed: true, presets: [{ label: '1억 이상', min: '10000' }, { label: '5억 이상', min: '50000' }, { label: '1억 미만', max: '10000' }, { label: '적자 기업만', loss: true }] },
];

const dday = (to: string | null | undefined): number | null => (to ? Math.floor((new Date(String(to).slice(0, 10) + 'T23:59:59').getTime() - Date.now()) / 86400000) : null);

export default function LeadCandidatePage({ onToast }: Props) {
  const [rows, setRows] = useState<Candidate[]>([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState<CandidateMeta | null>(null);
  const [filters, setFilters] = useState<CandidateFilters>({ page: 1, pageSize: 50 });
  useEffect(() => { setCandidateFilters({ region: filters.region, sigungu: filters.sigungu }); }, [filters.region, filters.sigungu]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [promote, setPromote] = useState<Candidate | null>(null);
  const [uploading, setUploading] = useState(false);
  const [anoms, setAnoms] = useState<{ list: UploadAnomaly[]; source: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listCandidates(filters);
      setRows(d.rows); setTotal(d.total); setSel(new Set());
      if (d.rows.length === 0 && d.total > 0 && filters.page > 1) setFilters((f) => ({ ...f, page: 1 }));
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setLoading(false); }
  }, [filters, onToast]);
  const loadMeta = useCallback(async () => {
    try { setMeta(await candidateMeta()); } catch (e) { onToast((e as Error).message, 'error'); }
  }, [onToast]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const set = (patch: Partial<CandidateFilters>) => setFilters((f) => ({ ...f, ...patch, page: 1 }));

  const onUpload = async (file: File) => {
    setUploading(true);
    onToast('업로드 처리 중… (연 4만행, 수 초 소요)');
    try {
      const r = await uploadCandidateCsv(file);
      onToast('신규 ' + r.inserted.toLocaleString() + '건 · 중복 ' + r.duplicated.toLocaleString() + '건 · 정보보강 ' + r.enriched.toLocaleString() + '건' + (r.corrected ? ' · 사업자번호 정정 ' + r.corrected.toLocaleString() + '건' : '') + (r.rescan ? ' · 재스캔 예약 ' + r.rescan.toLocaleString() + '건' : '') + (r.anomalies && r.anomalies.length ? ' · ⚠️ 특이사항 ' + r.anomalies.length + '건' : '') + ' · 불량 ' + r.skipped + '건 (' + r.source + ')');
      if (r.anomalies && r.anomalies.length) setAnoms({ list: r.anomalies, source: r.source });
      load(); loadMeta();
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setUploading(false); }
  };
  const doDelete = async () => {
    if (sel.size === 0) return;
    if (!window.confirm('선택한 후보 ' + sel.size + '건을 삭제할까요? (이미 승격된 리드에는 영향 없음)')) return;
    try { const r = await deleteCandidates(Array.from(sel)); onToast(r.deleted + '건 삭제'); load(); loadMeta(); }
    catch (e) { onToast((e as Error).message, 'error'); }
  };
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSel = rows.length > 0 && rows.every((c) => sel.has(c.id));

  /* 필터 카드 */
  const monthsVal = filters.expired_recent ? 'past' : (filters.expiring_months ? String(filters.expiring_months) : '');
  const setMonths = (v: string) => set({ expired_recent: v === 'past' ? true : undefined, expiring_months: v && v !== 'past' ? Number(v) : undefined });
  const rangeValues: RangeValues = { saleMin: filters.saleMin, saleMax: filters.saleMax, opMin: filters.opMin, opMax: filters.opMax, opLoss: filters.opLoss };
  const onRanges = (v: RangeValues) => set({ saleMin: v.saleMin as number | undefined, saleMax: v.saleMax as number | undefined, opMin: v.opMin as number | undefined, opMax: v.opMax as number | undefined, opLoss: !!v.opLoss });
  const chips: Chip[] = [];
  if (filters.q) chips.push({ k: '검색', v: filters.q, rm: () => set({ q: undefined }) });
  if (filters.region) chips.push({ k: '지역', v: filters.region + (filters.sigungu ? ' ' + filters.sigungu : ''), rm: () => set({ region: undefined, sigungu: undefined }) });
  if (monthsVal) chips.push({ k: '만료', v: ({ past: '만료 경과', 3: '3개월 이내', 6: '6개월 이내', 12: '12개월 이내', 24: '24개월 이내' } as Record<string, string>)[monthsVal] || monthsVal, rm: () => setMonths('') });
  if (filters.confirm_type) chips.push({ k: '유형', v: filters.confirm_type, rm: () => set({ confirm_type: undefined }) });
  if (filters.corp_no) chips.push({ k: '법인번호', v: filters.corp_no === 'has' ? '보유' : '미보유', rm: () => set({ corp_no: undefined }) });
  if (filters.converted && filters.converted !== 'all') chips.push({ k: '전환', v: filters.converted === 'yes' ? '전환됨' : '미전환', rm: () => set({ converted: undefined }) });
  const st = rangeLabel(filters.saleMin, filters.saleMax); if (st) chips.push({ k: '연매출', v: st, rm: () => set({ saleMin: undefined, saleMax: undefined }) });
  if (filters.opLoss) chips.push({ k: '영업이익', v: '적자', rm: () => set({ opLoss: false }) });
  else { const ot = rangeLabel(filters.opMin, filters.opMax); if (ot) chips.push({ k: '영업이익', v: ot, rm: () => set({ opMin: undefined, opMax: undefined }) }); }
  const resetAll = () => setFilters((f) => ({ page: 1, pageSize: f.pageSize }));

  /* 열 — ATV poolVenture 동일 */
  const today = new Date().toISOString().slice(0, 10);
  const cols: AtvCol<Candidate>[] = [
    { key: 'ceo', label: '대표자', w: 64, render: (c) => <span style={{ color: c.ceo_name ? undefined : '#9CA3AF' }}>{c.ceo_name || c.ceo_masked || ''}</span> },
    { key: 'bizno', label: '사업자번호', w: 94, render: (c) => (c.biz_no && c.biz_no.length === 10 ? fmtBizNo(c.biz_no) : <span style={{ color: '#D1D5DB' }}>-</span>) },
    { key: 'industry', label: '업종', w: 92, absorb: true, render: (c) => <span className="atv-addr" title={c.industry || c.product || ''}>{c.industry || c.product || ''}</span> },
    { key: 'region', label: '지역', w: 96, render: (c) => [c.region, c.sigungu].filter(Boolean).join(' ') },
    { key: 'estb', label: '설립', w: 58, render: (c) => (c.estb_dt ? String(c.estb_dt).slice(0, 4) : '') },
    { key: 'sale', label: '연매출', w: 76, cls: 'num', render: (c) => <FinCell v={c.fs_sale} fsYear={c.fs_year} /> },
    { key: 'op', label: '영업이익', w: 76, cls: 'num', render: (c) => <FinCell v={c.fs_op} fsYear={c.fs_year} /> },
    { key: 'periods', label: '유효기간 (전체 이력)', w: 176, render: (c) => {
      const ps = (c.periods && c.periods.length ? c.periods : [{ id: c.id, venture_from: c.venture_from, venture_to: c.venture_to, renew_type: c.renew_type }]);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '2px 0' }}>
          {ps.map((p) => { const t = p.venture_to ? String(p.venture_to).slice(0, 10) : ''; const valid = !!t && t >= today; return (
            <span key={p.id} style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: valid ? 700 : 400, color: valid ? '#111827' : '#8A9097' }}>
              {p.venture_from ? String(p.venture_from).slice(0, 10) : '—'} ~ {t || '—'}{p.renew_type ? <span style={{ fontSize: 10, marginLeft: 5, fontWeight: 400, color: '#6B7280' }}>{p.renew_type}</span> : null}
            </span>); })}
        </div>);
    } },
    { key: 'dday', label: '임박도', w: 78, render: (c) => {
      const d = dday(c.venture_to);
      if (d == null) return '—';
      const cls = d < 0 ? 'past' : d <= 30 ? 'd30' : d <= 90 ? 'd90' : 'd180';
      return <span className={'atv-dday ' + cls}>{d >= 0 ? 'D-' + d : 'D+' + (-d)}</span>;
    } },
    { key: 'conv', label: '리드', w: 90, render: (c) => (c.converted
      ? <span className="ld-year" style={{ background: '#F3F4F6', color: '#4B5563', margin: 0 }} title={c.fp_name ? '배정: ' + c.fp_name : '경정청구 대상으로 전환됨'}>전환됨{c.fp_name ? ' · ' + c.fp_name : ''}</span>
      : <button type="button" className="atv-abtn pri" onClick={(e) => { e.stopPropagation(); setPromote(c); }}>리드 승격</button>) },
  ];

  return (
    <div className="ld-root">
      <div className="ld-counts">
        <button className={'ld-count' + (!filters.confirm_type ? ' on' : '')} onClick={() => set({ confirm_type: undefined })}>전체 {meta ? meta.total.toLocaleString() : ''}</button>
        {TYPE_ORDER.filter((t) => meta?.counts[t]).map((t) => (
          <button key={t} className={'ld-count' + (filters.confirm_type === t ? ' on' : '')} onClick={() => set({ confirm_type: filters.confirm_type === t ? undefined : t })}>{t} {(meta?.counts[t] || 0).toLocaleString()}</button>
        ))}
        {meta && (() => { const etc = meta.total - TYPE_ORDER.reduce((s2, t) => s2 + (meta.counts[t] || 0), 0); return etc > 0 ? (
          <button className={'ld-count' + (filters.confirm_type === '기타' ? ' on' : '')} onClick={() => set({ confirm_type: filters.confirm_type === '기타' ? undefined : '기타' })}>기타 {etc.toLocaleString()}</button>
        ) : null; })()}
        <span style={{ width: 1, height: 18, background: '#E5E7EB', alignSelf: 'center', margin: '0 4px' }} />
        {([['', '전환 전체'], ['no', '미전환'], ['yes', '전환됨']] as [string, string][]).map(([k, l]) => (
          <button key={k || 'all'} className={'ld-count' + ((filters.converted && filters.converted !== 'all' ? filters.converted : '') === k ? ' on' : '')} onClick={() => set({ converted: (k || undefined) as CandidateFilters['converted'] })}>{l}</button>
        ))}
      </div>

      <AtvFilterCard
        searchPlaceholder="회사명 · 대표자 · 사업자번호 검색"
        q={filters.q || ''} onQ={(q) => set({ q: q || undefined })}
        find={(<>
          <select className="atv-fsel" value={monthsVal} onChange={(e) => setMonths(e.target.value)}>
            <option value="">전체 (역대 벤처기업 포함)</option>
            <option value="past">만료 경과 (3개월 내)</option>
            <option value="3">3개월 이내</option><option value="6">6개월 이내</option><option value="12">12개월 이내</option><option value="24">24개월 이내</option>
          </select>
          <select className="atv-fsel" value={filters.region || ''} onChange={(e) => set({ region: e.target.value || undefined, sigungu: undefined })}>
            <option value="">지역 전체</option>
            {(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {filters.region && (
            <select className="atv-fsel" value={filters.sigungu || ''} onChange={(e) => set({ sigungu: e.target.value || undefined })}>
              <option value="">시군구 전체</option>
              {(meta?.sigungus || []).filter((x) => x.region === filters.region).map((x) => <option key={x.sigungu} value={x.sigungu}>{x.sigungu}</option>)}
            </select>
          )}
          <select className="atv-fsel" value={filters.corp_no || ''} onChange={(e) => set({ corp_no: (e.target.value || undefined) as CandidateFilters['corp_no'] })} title="법인등록번호 보유 여부">
            <option value="">법인번호 전체</option><option value="has">법인번호 보유</option><option value="none">법인번호 미보유</option>
          </select>
        </>)}
        ranges={FIN_RANGES} rangeValues={rangeValues} onRanges={onRanges} panelLabel="재무 조건"
        hint="금액은 모두 만원 단위입니다 (예: 100000 = 10억) · 한쪽만 넣으면 이상 / 이하로 검색됩니다 · 영업이익은 -5000 처럼 음수도 됩니다 · 연매출·영업이익은 표에 보이는 최신 결산 값 기준입니다"
        view={(
          <select className="atv-fsel" value={filters.pageSize} onChange={(e) => set({ pageSize: Number(e.target.value) })} title="표시 개수">
            <option value={50}>50개씩</option><option value={500}>500개씩</option><option value={1000}>1000개씩</option>
          </select>
        )}
        total={total} grandTotal={meta ? meta.total : null} chips={chips} onResetAll={resetAll} selectedCount={sel.size} loading={loading}
      />

      <div className="ld-toolbar" style={{ marginBottom: 10 }}>
        <ScanControl region={filters.region} sigungu={filters.sigungu} selectedIds={Array.from(sel)} onToast={onToast} onFinished={() => { setSel(new Set()); load(); loadMeta(); }} />
        <input ref={fileRef} type="file" accept=".csv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }} />
        <span style={{ flex: 1 }} />
        {sel.size > 0 && <button className="ld-btn" style={{ color: '#DC2626', borderColor: '#FECACA' }} onClick={doDelete}>선택 {sel.size}건 삭제</button>}
      </div>

      <AtvTable<Candidate>
        storeKey="roum/pool-venture"
        rows={rows}
        rowKey={(c) => c.id}
        lead={[
          { key: 'chk', fw: 38, header: <input type="checkbox" title="화면 전체 선택" checked={allSel} onChange={(e) => setSel(e.target.checked ? new Set(rows.map((c) => c.id)) : new Set())} />, stopClick: true, render: (c) => <input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} /> },
          { key: 'no', fw: 38, header: 'No', cls: 'atv-no', render: (_c, i) => String((filters.page - 1) * filters.pageSize + i + 1) },
        ]}
        name={{ label: '회사명', w: 196, render: (c) => <span className="atv-co"><span className="nm" title={c.corp_name}>{c.corp_name}</span>{c.confirm_type ? <span className="flag" style={{ background: '#F3F4F6', color: '#4B5563', fontWeight: 600 }}>{c.confirm_type.replace('유형', '')}</span> : null}</span> }}
        cols={cols}
        empty={loading ? '불러오는 중…' : (chips.length ? '조건에 맞는 기업이 없습니다' : '후보가 없습니다. 벤처기업명단 CSV를 업로드하세요.')}
      />
      <AtvPager total={total} page={filters.page} pageSize={filters.pageSize} onPage={(p) => setFilters((f) => ({ ...f, page: p }))}
        left={<button disabled={uploading} onClick={() => fileRef.current?.click()} title="벤처기업명단 CSV 업로드 (연도별 파일 — 추가만, 충돌은 물어봄)" style={{ marginRight: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C0C4CC', textDecoration: 'underline dotted' }}>{uploading ? '업로드 중…' : '명단 CSV 업로드'}</button>} />
      <div className="atv-footnote">· 벤처확인 공개 명단 전체 풀(역대 포함) — 여러 번 갱신한 회사는 유효기간이 여러 줄로 표시됩니다 · 굵은 기간 = 현재 유효 · 만료일은 배치로 자동 최신화 · 연매출·영업이익은 스캔이 채운 최신 결산(연도 표기) · 열 경계선을 끌어 폭 조절(더블클릭 기본 폭 · Shift+더블클릭 전체 초기화)</div>

      {promote && <CandidatePromoteModal candidate={promote} onClose={() => setPromote(null)} onDone={() => { setPromote(null); load(); loadMeta(); }} onToast={onToast} />}
      {anoms && <UploadAnomalies anomalies={anoms.list} source={anoms.source} onClose={() => setAnoms(null)} />}
    </div>
  );
}
